import { InfoMessagePositionsEnum, OrderStatus, TimelineStep } from '@websky/graphql';
import { Boarding, Gate, Landing, Marker, ToAirport } from '../../../../../ServicesAggregation/src/Icons';
import { format, initI18n, MOBILE_MIN_WIDTH } from '../../../../../utils';
import * as React from 'react';
import cn from 'classnames';
import { useToggleable } from '../../../../../hooks';
import { useSelector } from 'react-redux';
import { getGdsServicesBySegments, getOrder } from '../../../../store/order/selectors';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../theme';
import { addTime, checkInternationalSegment, getSuggestedServices, getTime, getTimeline } from '../utils';
import Services from '../Services/Services';
import FlightInfoCard from '../../../../../FlightInfoCard';
import SuggestServices from '../SuggestServices/SuggestServices';
import ServiceAggregation from '../../../../../ServicesAggregation/src/ServiceAggregation';
import { ButtonBase } from '@material-ui/core';
import { ArrowForward } from '../../../../../Icons';
import PassengersDetails from '../../../../../PassengersDetails';
import Modal from '../../../../../Modal';
import { useMediaQuery } from 'react-responsive';
import { isAfter, parse } from 'date-fns';
import { cityIn } from 'lvovich';
import { getEveryTravelPrice } from '../../../../store/orderPrice/selectors';
import EveryTravelWrapper from '../EveryTravelWrapper/EveryTravelWrapper';
import OrderExareStatus from '../../OrderExareStatus/OrderExareStatus';
import InfoMessages from '../../../../../InfoMessages';
import { useConfig } from '../../../../../context';
const ServicesAggregationIconMap = {
    [TimelineStep.ToAirport]: ToAirport,
    [TimelineStep.Airport]: Gate,
    [TimelineStep.Boarding]: Boarding,
    [TimelineStep.Landing]: Landing,
    InCity: Marker
};
initI18n('ServiceAggregation');
const Inner = ({ segmentId, userInfo, refetchUser, setSegmentId, onNextSegment, goToRefund, goToExchange, everyTravelStyles, everyTravelSearchFormStyles, everyTravelCart, setEveryTravelCart, needToConfirmCart, goToChangePassenger }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { Engine: { nas: { suggestedServicesOrder } } } = useConfig();
    const { isOpen: isPassengersModalOpen, open: openPassengersModal, close: closePassengersModal } = useToggleable(false);
    const order = useSelector(getOrder);
    const servicesGroupsArray = useSelector(getGdsServicesBySegments);
    const isOrderCancelled = order.status === OrderStatus.Cancelled;
    const { segment } = order.flight.segments.find(segment => segment.segment.id === segmentId);
    const internationalSegment = checkInternationalSegment(segment);
    const { t } = useTranslation('ServiceAggregation');
    const theme = useTheme('Checkout').ServicesAggregationPage;
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const everyTravelPrice = useSelector(getEveryTravelPrice);
    const everyTravelSegmentId = (_b = (_a = order.additionalServices.everyTravel) === null || _a === void 0 ? void 0 : _a.initializeParameters) === null || _b === void 0 ? void 0 : _b.segmentId;
    const getServicesAggregationWithAllServices = React.useMemo(() => {
        return servicesGroupsArray.map(group => {
            if (everyTravelSegmentId === group.segmentId && everyTravelCart) {
                const services = [...group.services];
                everyTravelCart.items.forEach(item => {
                    var _a;
                    services.push({
                        type: 'everyTravel',
                        services: [
                            {
                                excursionId: item.excursionId,
                                productId: item.id,
                                title: item.name,
                                price: item.price,
                                imageUrl: item.image,
                                date: item.date && format(parse(item.date, 'yyyy-MM-dd', new Date()), 'dd MMM'),
                                time: (_a = item.time) === null || _a === void 0 ? void 0 : _a.substr(0, 5)
                            }
                        ],
                        selectedServices: [],
                        canAddService: (!everyTravelCart.isOrdered ||
                            order.status === OrderStatus.New ||
                            order.status === OrderStatus.Booked) &&
                            order.status !== OrderStatus.Cancelled,
                        step: 'InCity'
                    });
                });
                return Object.assign(Object.assign({}, group), { services });
            }
            return group;
        });
    }, [servicesGroupsArray, everyTravelCart, segmentId, everyTravelPrice, order.status]);
    const servicesByAggregationTypes = React.useMemo(() => getTimeline(getServicesAggregationWithAllServices.find(group => group.segmentId === segmentId), segmentId, internationalSegment), [getServicesAggregationWithAllServices, segmentId, internationalSegment]);
    const servicesGroups = React.useMemo(() => {
        const servicesGroups = [];
        servicesByAggregationTypes.forEach(group => {
            let timePeriod = group.type === TimelineStep.Landing ? segment.arrival.time : '';
            if (group.type === 'InCity' && !group.services.length) {
                return;
            }
            if (!timePeriod && group.type !== 'InCity') {
                if (group.minutes.from === group.minutes.to) {
                    timePeriod = t('at {{time}}', {
                        time: getTime(addTime(segment.departure.time, group.minutes.from * -1, segment.departure.date))
                    });
                }
                else {
                    timePeriod = t('{{timeFrom}}–{{timeTo}}', {
                        timeFrom: getTime(addTime(segment.departure.time, group.minutes.from * -1, segment.departure.date)),
                        timeTo: getTime(addTime(segment.departure.time, group.minutes.to * -1, segment.departure.date))
                    });
                }
            }
            const availableServices = group.services.filter(service => service.canAddService || !!service.selectedServices.length || service.type === 'everyTravel');
            let desc = '';
            if (group.type === TimelineStep.ToAirport) {
                desc = t(internationalSegment ? `${group.type}_international_desc` : `${group.type}_desc`);
            }
            else {
                desc = t(`${group.type}_desc`);
            }
            servicesGroups.push({
                type: group.type,
                icon: ServicesAggregationIconMap[group.type],
                directionLabel: t(group.type, {
                    departureCity: segment.departure.airport.city.name,
                    arrivalCity: group.type === 'InCity'
                        ? cityIn(segment.arrival.airport.city.name)
                        : segment.arrival.airport.city.name
                }),
                description: desc,
                timePeriod: timePeriod,
                services: React.createElement(Services, { segmentId: group.segmentId, services: availableServices }),
                empty: !availableServices.length
            });
        });
        return servicesGroups;
    }, [servicesByAggregationTypes]);
    const nextFlight = React.useMemo(() => {
        if (+segmentId < order.flight.segments.length - 1) {
            const { arrival, departure } = order.flight.segments[+segmentId + 1].segment;
            return {
                arrival: arrival.airport.city.name,
                departure: departure.airport.city.name
            };
        }
        return null;
    }, [segmentId]);
    const suggestedServices = React.useMemo(() => {
        const servicesOnSegment = servicesGroupsArray.find(group => group.segmentId === segmentId);
        return getSuggestedServices(servicesOnSegment, suggestedServicesOrder);
    }, [servicesGroupsArray, segmentId, suggestedServicesOrder]);
    const everyTravel = order.additionalServices.everyTravel;
    const isEveryTravelAvailable = React.useMemo(() => {
        var _a, _b, _c, _d;
        const arrDate = (_b = (_a = order.additionalServices.everyTravel) === null || _a === void 0 ? void 0 : _a.initializeParameters) === null || _b === void 0 ? void 0 : _b.arrDate, depDate = (_d = (_c = order.additionalServices.everyTravel) === null || _c === void 0 ? void 0 : _c.initializeParameters) === null || _d === void 0 ? void 0 : _d.depDate;
        if (arrDate) {
            if (!depDate) {
                return true;
            }
            return isAfter(new Date(depDate), new Date(arrDate));
        }
        return false;
    }, [
        (_d = (_c = order.additionalServices.everyTravel) === null || _c === void 0 ? void 0 : _c.initializeParameters) === null || _d === void 0 ? void 0 : _d.arrDate,
        (_f = (_e = order.additionalServices.everyTravel) === null || _e === void 0 ? void 0 : _e.initializeParameters) === null || _f === void 0 ? void 0 : _f.depDate
    ]);
    return (React.createElement("div", { className: theme.wrapper },
        React.createElement(InfoMessages, { className: cn(theme.container, theme.topInfoMessages), position: [InfoMessagePositionsEnum.bookingTop, InfoMessagePositionsEnum.default], messages: order.messages }),
        React.createElement(OrderExareStatus, { goToChangePassenger: goToChangePassenger, goToRefund: goToRefund, goToExchange: goToExchange }),
        React.createElement(FlightInfoCard, { segmentId: segmentId, setSegmentId: segmentId => setSegmentId(segmentId.toString()), onPassengerDetails: openPassengersModal, order: order, className: theme.container, onControlTicket: () => { }, onDownloadTickets: () => { } }),
        !isOrderCancelled && (React.createElement(React.Fragment, null,
            React.createElement(SuggestServices, { services: suggestedServices, segmentId: segmentId }),
            React.createElement(InfoMessages, { position: InfoMessagePositionsEnum.serviceTop, messages: order.messages, className: theme.container }),
            React.createElement(ServiceAggregation, { serviceGroups: servicesGroups }),
            React.createElement(InfoMessages, { position: InfoMessagePositionsEnum.serviceBottom, messages: order.messages, className: theme.container }),
            everyTravel && isEveryTravelAvailable && (React.createElement("div", { style: { display: segmentId === everyTravelSegmentId ? 'block' : 'none' } },
                React.createElement(EveryTravelWrapper, { everyTravel: everyTravel, segment: segment, order: order, setEveryTravelCart: setEveryTravelCart, everyTravelStyles: everyTravelStyles, everyTravelSearchFormStyles: everyTravelSearchFormStyles, needToConfirmCart: needToConfirmCart }))),
            nextFlight && (React.createElement("div", { className: theme.buttons },
                React.createElement(ButtonBase, { className: theme.next, onClick: onNextSegment },
                    t('Next flight {{departure}} — {{arrival}}', {
                        departure: nextFlight.departure,
                        arrival: nextFlight.arrival
                    }),
                    ArrowForward))))),
        React.createElement(Modal, { open: isPassengersModalOpen, bottomDrawer: isMobile, onClose: closePassengersModal, closeButtonRenderer: () => null, classes: {
                paper: theme.paper
            } },
            React.createElement(PassengersDetails, { orderId: order.id, orderStatus: order.status, travellers: order.travellers, changeTravellersAvailability: (_g = order.exareInfo) === null || _g === void 0 ? void 0 : _g.changeTravellerDataAvailability, goToChangePassenger: goToChangePassenger, onClose: closePassengersModal }))));
};
export default Inner;
